import { useState, useMemo } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import BuildIcon from '@mui/icons-material/Build';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { StaffRole } from '@/types';
import type { InvitationApiResponse } from '@/features/settings/SettingsPage';
import type { DataTableColumn } from '@/components/common';
import { DataTable, ConfirmDialog, UserAvatar } from '@/components/common';
import { useAuthStore } from '@/stores/authStore';
import { ResetMemberPasswordDialog } from './ResetMemberPasswordDialog';
import { memberRoleLabel, type TeamMember } from './staffRoleLabels';
import { dateFnsLocale } from '@/utils/locale';

export type { TeamMember };

type InvitationRow = InvitationApiResponse & {
  roleId?: string | null;
  roleName?: string | null;
};

interface TeamMembersListProps {
  members: TeamMember[];
  invitations: InvitationRow[];
  onAdd: () => void;
  onEdit: (member: TeamMember) => void;
  onToggleActive: (memberId: string, isActive: boolean) => void;
  /** Whether the member counts toward workshop capacity (#3120). */
  onToggleSchedulable: (memberId: string, isSchedulable: boolean) => void;
  onDelete: (memberId: string) => void;
  onResendInvitation: (invitationId: string) => void;
  onCancelInvitation: (invitationId: string) => void;
  /**
   * Mint a fresh invitation and show its mobile join QR (#1244 E4). Rotates the
   * token, hence the confirmation below — the invitation token is hashed at
   * rest, so the original code cannot be re-displayed.
   *
   * Returns a promise because the confirmation has to stay open until it
   * settles: every call rotates the token, so two in flight would rotate twice
   * and could resolve out of order, leaving the dialog showing a code that has
   * already been invalidated by the later request.
   */
  onShowInviteQr: (invitationId: string) => Promise<void>;
  /** Admin reset of an OS Buddy password. Hidden on Paya ID hosts. */
  canResetPassword?: boolean;
  /** Open the competence matrix filtered to this staff member (any role). */
  onManageSkills?: (member: TeamMember) => void;
}

type MemberOrInvitation =
  | { kind: 'member'; data: TeamMember }
  | { kind: 'invitation'; data: InvitationRow };

type StatusFilter = 'active' | 'inactive' | 'pending';

const roleConfig: Record<StaffRole, { color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' }> = {
  owner: { color: 'secondary' },
  admin: { color: 'primary' },
  manager: { color: 'info' },
  mechanic: { color: 'success' },
  receptionist: { color: 'default' },
};

/** Map backend role to frontend StaffRole for display */
function mapInvitationRole(backendRole: string): StaffRole {
  switch (backendRole) {
    case 'admin': return 'owner';
    case 'reception': return 'receptionist';
    default: return backendRole as StaffRole;
  }
}

export function TeamMembersList({
  members,
  invitations,
  onAdd,
  onEdit,
  onToggleActive,
  onToggleSchedulable,
  onDelete,
  onResendInvitation,
  onCancelInvitation,
  onShowInviteQr,
  canResetPassword = false,
  onManageSkills,
}: TeamMembersListProps) {
  const { t } = useTranslation('settings');
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TeamMember | null>(null);
  const [resetTarget, setResetTarget] = useState<TeamMember | null>(null);
  const [invMenuAnchor, setInvMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<InvitationRow | null>(null);
  const [qrConfirmTarget, setQrConfirmTarget] = useState<InvitationRow | null>(null);
  const [qrPending, setQrPending] = useState(false);
  const [filters, setFilters] = useState<StatusFilter[]>(['active', 'pending']);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, member: TeamMember) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedMember(null);
  };

  const handleInvMenuOpen = (event: React.MouseEvent<HTMLElement>, invitation: InvitationRow) => {
    event.stopPropagation();
    setInvMenuAnchor(event.currentTarget);
    setSelectedInvitation(invitation);
  };

  const handleInvMenuClose = () => {
    setInvMenuAnchor(null);
    setSelectedInvitation(null);
  };

  // Build unified list
  const allItems: MemberOrInvitation[] = useMemo(() => {
    const invItems: MemberOrInvitation[] = invitations.map((inv) => ({ kind: 'invitation', data: inv }));
    const memberItems: MemberOrInvitation[] = members.map((m) => ({ kind: 'member', data: m }));
    return [...invItems, ...memberItems];
  }, [members, invitations]);

  // Filter
  const filteredItems = useMemo(() => {
    if (filters.length === 0) return [];
    return allItems.filter((item) => {
      if (item.kind === 'invitation') return filters.includes('pending');
      if (item.data.isActive) return filters.includes('active');
      return filters.includes('inactive');
    });
  }, [allItems, filters]);

  // Counts for quick filters
  const activeCount = members.filter((m) => m.isActive).length;
  const inactiveCount = members.filter((m) => !m.isActive).length;
  const pendingCount = invitations.length;

  const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, newFilters: StatusFilter[]) => {
    // Prevent deselecting all — at least one must stay active
    if (newFilters.length > 0) {
      setFilters(newFilters);
    }
  };

  const getName = (item: MemberOrInvitation): string => {
    if (item.kind === 'invitation') {
      const inv = item.data;
      return inv.firstName && inv.lastName ? `${inv.firstName} ${inv.lastName}` : inv.email;
    }
    return `${item.data.firstName} ${item.data.lastName}`;
  };

  const getRole = (item: MemberOrInvitation): string => {
    return item.kind === 'invitation' ? mapInvitationRole(item.data.role) : item.data.role;
  };

  const sortCompareFn = (a: MemberOrInvitation, b: MemberOrInvitation, key: string): number => {
    switch (key) {
      case 'name':
        return getName(a).localeCompare(getName(b), 'fi');
      case 'role':
        return getRole(a).localeCompare(getRole(b));
      case 'status': {
        const statusOrder = (item: MemberOrInvitation) => {
          if (item.kind === 'invitation') return 2;
          return item.data.isActive ? 0 : 1;
        };
        return statusOrder(a) - statusOrder(b);
      }
      default:
        return 0;
    }
  };

  const columns: DataTableColumn<MemberOrInvitation>[] = [
    {
      header: t('member'),
      sortKey: 'name',
      renderCell: (item) => {
        if (item.kind === 'invitation') {
          const inv = item.data;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'warning.light' }}>
                <MailOutlineIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {inv.firstName && inv.lastName
                    ? `${inv.firstName} ${inv.lastName}`
                    : inv.email}
                </Typography>
                {inv.firstName && inv.lastName && (
                  <Typography variant="caption" color="text.secondary">
                    {inv.email}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }
        const member = item.data;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <UserAvatar
              firstName={member.firstName}
              lastName={member.lastName}
              avatarUrl={member.avatarUrl}
              sx={!member.isActive ? { opacity: 0.5, filter: 'grayscale(1)' } : undefined}
            />
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {member.firstName} {member.lastName}
              </Typography>
              {member.skills && member.skills.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {member.skills.slice(0, 2).join(', ')}
                  {member.skills.length > 2 && ` +${String(member.skills.length - 2)}`}
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      header: t('role'),
      sortKey: 'role',
      renderCell: (item) => {
        const role = item.kind === 'invitation'
          ? mapInvitationRole(item.data.role)
          : item.data.role;
        const color = roleConfig[role]?.color ?? 'default';
        return (
          <Chip
            label={memberRoleLabel(item.data.roleName, role, t)}
            size="small"
            color={color}
          />
        );
      },
    },
    {
      header: t('contact'),
      renderCell: (item) => {
        if (item.kind === 'invitation') {
          return <Typography variant="body2">{item.data.email}</Typography>;
        }
        const member = item.data;
        return (
          <>
            <Typography variant="body2">{member.email}</Typography>
            <Typography variant="caption" color="text.secondary">
              {member.phone}
            </Typography>
          </>
        );
      },
    },
    {
      header: t('status'),
      sortKey: 'status',
      renderCell: (item) => {
        if (item.kind === 'invitation') {
          return (
            <Chip
              label={t('pendingInvitation')}
              size="small"
              color="warning"
              variant="outlined"
            />
          );
        }
        const member = item.data;
        return (
          <Chip
            label={member.isActive ? t('active') : t('inactive')}
            size="small"
            color={member.isActive ? 'success' : 'default'}
            variant={member.isActive ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      header: t('lastActive'),
      renderCell: (item) => {
        if (item.kind === 'invitation') {
          return (
            <Typography variant="body2" color="text.secondary">
              {t('invited')}{' '}
              {formatDistanceToNow(new Date(item.data.createdAt), {
                addSuffix: true,
                locale: dateFnsLocale(),
              })}
            </Typography>
          );
        }
        const member = item.data;
        if (member.lastLoginAt) {
          return (
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(new Date(member.lastLoginAt), {
                addSuffix: true,
                locale: dateFnsLocale(),
              })}
            </Typography>
          );
        }
        return (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        );
      },
    },
    {
      header: '',
      align: 'center',
      renderCell: (item) => {
        if (item.kind === 'invitation') {
          return (
            <Tooltip title={t('moreOptions')}>
              <IconButton size="small" onClick={(e) => { handleInvMenuOpen(e, item.data); }} aria-label={t('moreOptions')} sx={{ minWidth: 44, minHeight: 44 }}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={t('moreOptions')}>
            <IconButton size="small" onClick={(e) => { handleMenuOpen(e, item.data); }} aria-label={t('moreOptions')} sx={{ minWidth: 44, minHeight: 44 }}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {t('teamMembers')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeCount} {t('active')}, {inactiveCount} {t('inactive')}
            {pendingCount > 0 && `, ${String(pendingCount)} ${t('pendingInvitations')}`}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
          {t('addMember')}
        </Button>
      </Box>

      <ToggleButtonGroup
        value={filters}
        onChange={handleFilterChange}
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="active">
          {t('filterActive')} ({String(activeCount)})
        </ToggleButton>
        <ToggleButton value="pending">
          {t('filterPending')} ({String(pendingCount)})
        </ToggleButton>
        <ToggleButton value="inactive">
          {t('filterInactive')} ({String(inactiveCount)})
        </ToggleButton>
      </ToggleButtonGroup>

      <DataTable<MemberOrInvitation>
        columns={columns}
        data={filteredItems}
        getRowKey={(item) =>
          item.kind === 'invitation' ? `inv-${item.data.id}` : item.data.id
        }
        onRowClick={(item) => {
          if (item.kind === 'member') onEdit(item.data);
        }}
        sortCompareFn={sortCompareFn}
        defaultSortKey="name"
        renderMobileCard={(item) => {
          if (item.kind === 'invitation') {
            const inv = item.data;
            const displayRole = mapInvitationRole(inv.role);
            const displayColor = roleConfig[displayRole]?.color ?? 'default';
            return (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'warning.light', width: 36, height: 36 }}>
                    <MailOutlineIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {inv.firstName && inv.lastName
                        ? `${inv.firstName} ${inv.lastName}`
                        : inv.email}
                    </Typography>
                    <Chip label={memberRoleLabel(inv.roleName, displayRole, t)} size="small" color={displayColor} sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={t('pendingInvitation')} size="small" color="warning" variant="outlined" />
                  <Tooltip title={t('moreOptions')}>
                    <IconButton size="small" onClick={(e) => { handleInvMenuOpen(e, inv); }} aria-label={t('moreOptions')} sx={{ minWidth: 44, minHeight: 44 }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          }
          const member = item.data;
          return (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <UserAvatar
                  firstName={member.firstName}
                  lastName={member.lastName}
                  avatarUrl={member.avatarUrl}
                  size={36}
                  sx={!member.isActive ? { opacity: 0.5, filter: 'grayscale(1)' } : undefined}
                />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {member.firstName} {member.lastName}
                  </Typography>
                  <Chip label={memberRoleLabel(member.roleName, member.role, t)} size="small" color={roleConfig[member.role]?.color ?? 'default'} sx={{ height: 18, fontSize: '0.65rem' }} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip
                  label={member.isActive ? t('active') : t('inactive')}
                  size="small"
                  color={member.isActive ? 'success' : 'default'}
                  variant={member.isActive ? 'filled' : 'outlined'}
                />
                <Tooltip title={t('moreOptions')}>
                  <IconButton size="small" onClick={(e) => { handleMenuOpen(e, member); }} aria-label={t('moreOptions')} sx={{ minWidth: 44, minHeight: 44 }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        }}
        emptyMessage={t('noMembers')}
        ariaLabel={t('teamMembers')}
      />

      {/* Member Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedMember) onEdit(selectedMember);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('edit')}</ListItemText>
        </MenuItem>
        {canResetPassword && selectedMember && selectedMember.id !== currentUserId && (
          <MenuItem
            onClick={() => {
              setResetTarget(selectedMember);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <LockResetIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('resetMemberPassword.action')}</ListItemText>
          </MenuItem>
        )}
        {onManageSkills && selectedMember && (
          <MenuItem
            onClick={() => {
              onManageSkills(selectedMember);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <BuildIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('mechanicSkills.manageSkills')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (selectedMember) onToggleActive(selectedMember.id, !selectedMember.isActive);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            {selectedMember?.isActive ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedMember?.isActive ? t('deactivate') : t('activate')}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedMember) onToggleSchedulable(selectedMember.id, !(selectedMember.isSchedulable ?? true));
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            {(selectedMember?.isSchedulable ?? true) ? (
              <EventBusyIcon fontSize="small" />
            ) : (
              <EventAvailableIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText secondary={t('memberSchedulableHint')} secondaryTypographyProps={{ sx: { whiteSpace: 'normal', maxWidth: 280 } }}>
            {(selectedMember?.isSchedulable ?? true) ? t('memberSchedulableTurnOff') : t('memberSchedulableTurnOn')}
          </ListItemText>
        </MenuItem>
        {selectedMember?.role !== 'owner' && (
          <MenuItem
            onClick={() => {
              setDeleteTarget(selectedMember);
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{t('delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Invitation Actions Menu */}
      <Menu
        anchorEl={invMenuAnchor}
        open={Boolean(invMenuAnchor)}
        onClose={handleInvMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedInvitation) onResendInvitation(selectedInvitation.id);
            handleInvMenuClose();
          }}
        >
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('resendInvite')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            // Capture the row before the menu close clears the selection.
            setQrConfirmTarget(selectedInvitation);
            handleInvMenuClose();
          }}
        >
          <ListItemIcon>
            <QrCode2Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('mobileJoinQr.showAction')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedInvitation) onCancelInvitation(selectedInvitation.id);
            handleInvMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <CancelIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('cancelInvite')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('confirmDelete.title')}
        message={t('confirmDelete.memberMessage', { name: deleteTarget ? `${deleteTarget.firstName} ${deleteTarget.lastName}` : '' })}
        confirmText={t('delete')}
        confirmColor="error"
        onConfirm={() => {
          if (deleteTarget) onDelete(deleteTarget.id);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/*
        Showing a mobile QR necessarily mints a new invitation token — the stored
        one is a hash — so the link the invitee may already hold stops working.
        That is worth a confirmation rather than a surprise.
      */}
      <ConfirmDialog
        open={!!qrConfirmTarget}
        title={t('mobileJoinQr.showConfirmTitle')}
        message={t('mobileJoinQr.showConfirmMessage', { email: qrConfirmTarget?.email ?? '' })}
        confirmText={t('mobileJoinQr.showConfirmAction')}
        // Stays open while the mint is in flight, which disables both buttons —
        // a second confirm would rotate the token again and the two responses
        // could land out of order, showing an already-invalidated code.
        loading={qrPending}
        onConfirm={() => {
          if (!qrConfirmTarget || qrPending) return;
          setQrPending(true);
          void onShowInviteQr(qrConfirmTarget.id)
            // The caller reports its own failure; swallowing here only stops a
            // rejection from leaving the dialog disabled forever (and from
            // surfacing as an unhandled rejection).
            .catch(() => undefined)
            .finally(() => {
              setQrPending(false);
              setQrConfirmTarget(null);
            });
        }}
        onCancel={() => setQrConfirmTarget(null)}
      />

      {resetTarget && (
        <ResetMemberPasswordDialog
          open
          member={{
            id: resetTarget.id,
            name: `${resetTarget.firstName} ${resetTarget.lastName}`,
          }}
          onClose={() => { setResetTarget(null); }}
        />
      )}
    </Box>
  );
}
